import React, { useState, useMemo } from "react";
import { normalizeText } from "../../utils/text.js";

const BURGUNDY = "#6E1A36";
const LINE = "#ECE5E2";
const CREAM = "#FFF6EE";
const INK = "#1F1620";
const INK_3 = "#8A7780";

function initialsOf(user) {
  return `${user?.name?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase();
}

// Recherche + tags pour sélectionner plusieurs utilisateurs — reprend
// exactement le comportement de la maquette (shell.jsx, EventTaskModal) :
// jusqu'à 6 suggestions affichées dès le focus (même sans texte tapé),
// masquées après un court délai au blur pour laisser le clic s'exécuter.
function UserSearchTagPicker({
  allUsers,
  selectedIds,
  onAdd,
  onRemove,
  label,
  required = false,
  placeholder = "Rechercher un utilisateur…",
  emptyText = "Aucun utilisateur ajouté.",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showList, setShowList] = useState(false);

  const userMap = allUsers.reduce((map, u) => {
    map[u.id] = u;
    return map;
  }, {});

  const suggestions = useMemo(() => {
    const q = normalizeText(searchTerm);
    return allUsers
      .filter((u) => !selectedIds.includes(u.id))
      .filter((u) => !q || normalizeText(`${u.name} ${u.lastName}`).includes(q))
      .slice(0, 6);
  }, [searchTerm, selectedIds, allUsers]);

  const handleSelect = (user) => {
    if (!selectedIds.includes(user.id)) {
      onAdd(user);
    }
    setSearchTerm("");
    setShowList(false);
  };

  return (
    <div>
      {label && (
        <label
          className="block mb-1"
          style={{ fontSize: 12.5, fontWeight: 500, color: "#4D3F45" }}
        >
          {label}{" "}
          {required && (
            <span style={{ color: "#C03A2B" }} className="font-normal italic">
              (obligatoire*)
            </span>
          )}
        </label>
      )}

      <div className="relative w-full">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowList(true)}
          onBlur={() => setTimeout(() => setShowList(false), 150)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full h-10 rounded-lg px-3 text-sm outline-none transition-all border"
          style={{ borderColor: LINE, background: "#fff", color: INK, fontSize: 13 }}
        />

        {showList && suggestions.length > 0 && (
          <ul
            className="absolute left-0 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-lg border bg-white z-20"
            style={{ borderColor: LINE, boxShadow: "0 10px 28px rgba(0,0,0,.10)" }}
          >
            {suggestions.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onMouseDown={() => handleSelect(user)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors"
                  style={{ fontSize: 13, color: INK }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = CREAM)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 22, height: 22, background: BURGUNDY, color: "#fff", fontSize: 10, fontWeight: 600 }}
                  >
                    {initialsOf(user)}
                  </span>
                  <span>
                    {user.name} {user.lastName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-2">
        {selectedIds.length === 0 ? (
          <p style={{ fontSize: 12.5, color: INK_3 }} className="italic">
            {emptyText}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedIds.map((id) => {
              const user = userMap[id];
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-white"
                  style={{ borderColor: LINE, padding: "4px 6px 4px 4px", fontSize: 12, color: INK }}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 18, height: 18, background: BURGUNDY, color: "#fff", fontSize: 9, fontWeight: 600 }}
                  >
                    {initialsOf(user)}
                  </span>
                  {user?.name || "Utilisateur inconnu"} {user?.lastName || ""}
                  <button
                    type="button"
                    onClick={() => onRemove(id)}
                    className="inline-flex items-center rounded-full p-0.5 transition-colors"
                    style={{ color: INK_3 }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = CREAM;
                      e.currentTarget.style.color = BURGUNDY;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = INK_3;
                    }}
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserSearchTagPicker;
